import * as Yup from 'yup';
import { FC, useState } from 'react';
import { useFormik } from 'formik';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  MenuItem,
  Typography
} from '@mui/material';
import TextField from '@mui/material/TextField';
import request from '../../../../utils/request';
import { LoadingButton } from '@mui/lab';
import SnackbarAlert from '../../../common/SnackbarAlert';
import React from 'react';
type CreateEditProps = {
  closeCreateEditForm: any;
  voyageEditDatas: any;
  editStatus: boolean;
  productId: any;
  setNewValue?: Function;
};
const CreateEdit: FC<CreateEditProps> = ({
  closeCreateEditForm,
  voyageEditDatas,
  editStatus,
  productId,
  setNewValue
}) => {
  const VoyageSchema = Yup.object().shape({
    voyageName: Yup.string()
      .required('Voyage Name is required')
      .min(3, 'Voyage Name must be at least 3 characters')
      .test(
        'notOnlyWhiteSpace',
        'Voyage Name cannot include leading and trailing spaces',
        (value) => {
          return !/\s{2,}/.test(value);
        }
      ),
      // .matches(
      //   // eslint-disable-next-line no-useless-escape
      //   /^[a-zA-Z0-9\s\[\]\-\(\)&\/\\<>]*$/,
      //   'Voyage Name only accepts special characters like &,[,],-,/,(,),<,>'
      // ),
    voyageCode: Yup.string()
      .required('Voyage Code is required')
      .test(
        'notOnlyWhiteSpace',
        'Voyage Code cannot include leading and trailing spaces',
        (value) => {
          return !/\s{2,}/.test(value);
        }
      )
      .min(2, 'Voyage Code must be at least 2 characters'),
      // .matches(
      //   // eslint-disable-next-line no-useless-escape
      //   /^[a-zA-Z0-9\s\[\]\-\(\)&\/\\]*$/,
      //   'Voyage Code only accepts special characters like &,[,],-,/,(,)'
      // ),
    voyageType: Yup.string()
      .required('Voyage Type is required')

      .trim('Voyage Type cannot include leading and trailing spaces')
      .strict(true),
    voyageDetails: Yup.string()
      .notRequired()
      .min(3, 'Voyage Detail must be at least 3 characters')

      .trim('Voyage Details cannot include leading and trailing spaces')
      .strict(true)
      .matches(
        /(?:[a-zA-Z0-9]+[^\W_]+)|(?:[^\W_]+[a-zA-Z0-9]+)/,
        'Voyage Detail is not valid'
      )
      .matches(/^[a-zA-Z].*$/, 'Voyage Detail must start with an alphabet')

      .matches(
        /^[^-!@#$%^&*()_+={};:'",.<>/?\\|[\]`~].*$/,
        'Voyage Detail cannot start with a special character'
      )
      .nullable()
  });

  const [loader, setLoader] = useState(false);
  const [showSnackBarAlert, setShowSnackBarAlert] = useState({
    status: false,
    type: 'success',
    message: null
  });
  const formik = useFormik({
    initialValues: {
      id: '',
      voyageType: '',
      voyageDetails: '',
      status: true,
      voyageName: '',
      voyageCode: '',
      productId: productId
    },
    validationSchema: VoyageSchema,
    onSubmit: (values) => {
      setLoader(true);
      const method = editStatus ? 'PUT' : 'POST';
      request({
        url: 'voyage',
        method: method,
        data: values
      })
        .then((voyage) => {
          if (voyage.status) {
            closeCreateEditForm(true);
            setLoader(false);
            if (setNewValue) {
              setNewValue(values?.voyageName);
            }
          } else {
            setLoader(false);
            setShowSnackBarAlert({
              status: true,
              type: 'error',
              message: voyage['message']
            });
          }
        })
        .catch(function (err) {
          setShowSnackBarAlert({
            status: true,
            type: 'error',
            message: err.message
          });
          setLoader(false);
        });
    }
  });

  const capitalizeFirst = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  if (editStatus) {
    formik.values.id = voyageEditDatas.id;
    formik.initialValues.voyageType = voyageEditDatas.voyageType;
    formik.initialValues.voyageDetails = voyageEditDatas.voyageDetails;
    formik.initialValues.voyageName = voyageEditDatas?.voyageName;
    formik.initialValues.voyageCode = voyageEditDatas?.voyageCode;
  }
  const handleSnackbarClose = () => {
    setShowSnackBarAlert({ status: false, type: null, message: null });
  };

  return (
    <>
      <SnackbarAlert
        showSnackBarAlert={showSnackBarAlert}
        handleSnackbarClose={handleSnackbarClose}
      />
      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12} className="ListContainer">
            <Card>
              {editStatus ? (
                <CardHeader title="Update Voyage" />
              ) : (
                <CardHeader title="Create Voyage" />
              )}
              <Divider />

              <CardContent>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label={
                          <React.Fragment>
                            Voyage Name{' '}
                            <Typography component="span" sx={{ color: 'red' }}>
                              *
                            </Typography>
                          </React.Fragment>
                        }
                        sx={{ width: '100%' }}
                        size="small"
                        name="voyageName"
                        value={capitalizeFirst(formik.values.voyageName)}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          formik.setFieldValue('voyageName', trimmedValue);
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.voyageName &&
                          Boolean(formik.errors.voyageName)
                        }
                        helperText={
                          formik.touched.voyageName && formik.errors.voyageName
                        }
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label={
                          <React.Fragment>
                            Voyage Code{' '}
                            <Typography component="span" sx={{ color: 'red' }}>
                              *
                            </Typography>
                          </React.Fragment>
                        }
                        sx={{ width: '100%' }}
                        size="small"
                        name="voyageCode"
                        value={formik.values.voyageCode}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          formik.setFieldValue('voyageCode', trimmedValue);
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.voyageCode &&
                          Boolean(formik.errors.voyageCode)
                        }
                        helperText={
                          formik.touched.voyageCode && formik.errors.voyageCode
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        select
                        name="voyageType"
                        sx={{ width: '100%' }}
                        size="small"
                        type="text"
                        label={
                          <React.Fragment>
                            Select Voyage Type{' '}
                            <Typography component="span" sx={{ color: 'red' }}>
                              *
                            </Typography>
                          </React.Fragment>
                        }
                        value={formik.values.voyageType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.voyageType &&
                          Boolean(formik.errors.voyageType)
                        }
                        helperText={
                          formik.touched.voyageType && formik.errors.voyageType
                        }
                      >
                        <MenuItem value="Domestic">
                          Domestic-Anywhere in India To Anywhere in India
                        </MenuItem>
                        <MenuItem value="Exports">
                          Exports-Anywhere in India To Anywhere in the World
                        </MenuItem>
                        <MenuItem value="Imports">
                          Imports-Anywhere from the Word to Anywhere in India
                        </MenuItem>
                        <MenuItem value="Anywhere from the World to Anywhere in the World">
                          Anywhere from the World to Anywhere in the World
                        </MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label={<React.Fragment>Voyage Details </React.Fragment>}
                        sx={{ width: '100%' }}
                        size="medium"
                        type="text-area"
                        multiline
                        rows={12}
                        name="voyageDetails"
                        value={capitalizeFirst(formik.values.voyageDetails)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.voyageDetails &&
                          Boolean(formik.errors.voyageDetails)
                        }
                        helperText={
                          formik.touched.voyageDetails &&
                          formik.errors.voyageDetails
                        }
                      />
                    </Grid>

                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={3}
                      mt={2}
                    >
                      {loader ? (
                        <LoadingButton
                          sx={{ mt: { xs: 2, md: 0 } }}
                          loading={loader}
                          variant="contained"
                          type="submit"
                        >
                          Save
                        </LoadingButton>
                      ) : (
                        <Button
                          sx={{ mt: { xs: 2, md: 0 } }}
                          type="submit"
                          variant="contained"
                        >
                          Save
                        </Button>
                      )}
                      &nbsp;
                      <Button
                        sx={{ mt: { xs: 2, md: 0 } }}
                        variant="contained"
                        color="error"
                        onClick={closeCreateEditForm}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default CreateEdit;
